import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { hasPendingSessionScans } from '../pages/admin/scanner/session-scan-vault';
import { acquirePwaUpdateLock, canApplyPwaUpdate, isPwaUpdateReserved, subscribePwaUpdateLocks, subscribePwaUpdateReservations } from './update-lock';
import { attachPwaUpdateCoordinator, PWA_UPDATE_MAX_DURATION_MS } from './pwa-coordination';
import { requestUpdateActivation, type UpdateActivationResult } from './request-update';

interface InstallPromptEvent extends Event {
  readonly prompt: () => Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>;
}

interface PwaContextValue {
  readonly offline: boolean;
  readonly updateAvailable: boolean;
  readonly updateActivated: boolean;
  readonly updateBlocked: boolean;
  readonly updatePreparing: boolean;
  readonly updateCoordinating: boolean;
  readonly updateFailure: Exclude<UpdateActivationResult, 'accepted'> | undefined;
  readonly canInstall: boolean;
  readonly iosInstallHint: boolean;
  readonly requestInstall: () => Promise<void>;
  readonly activateUpdate: () => void;
  readonly reloadUpdatedApp: () => void;
}

const PwaContext = createContext<PwaContextValue | undefined>(undefined);

export function PwaProvider({ children }: { readonly children: ReactNode }) {
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const [waiting, setWaiting] = useState<ServiceWorker | undefined>();
  const [updateActivated, setUpdateActivated] = useState(false);
  const [updateBlocked, setUpdateBlocked] = useState(() => !canApplyPwaUpdate());
  const [updatePreparing, setUpdatePreparing] = useState(false);
  const [updateCoordinating, setUpdateCoordinating] = useState(() => isPwaUpdateReserved());
  const [updateFailure, setUpdateFailure] = useState<Exclude<UpdateActivationResult, 'accepted'>>();
  const updateFlight = useRef(false);
  const updateCleanup = useRef<(() => void) | undefined>(undefined);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | undefined>();
  const [standalone, setStandalone] = useState(() => window.matchMedia?.('(display-mode: standalone)').matches
    || Boolean((navigator as Navigator & { readonly standalone?: boolean }).standalone));

  useEffect(() => {
    const syncConnectivity = () => { setOffline(!navigator.onLine); setUpdateBlocked(!canApplyPwaUpdate() || isPwaUpdateReserved()); setUpdateCoordinating(isPwaUpdateReserved()); };
    window.addEventListener('online', syncConnectivity);
    window.addEventListener('offline', syncConnectivity);
    const release = subscribePwaUpdateLocks(syncConnectivity);
    const releaseReservation = subscribePwaUpdateReservations(syncConnectivity);
    const interval = window.setInterval(syncConnectivity, 1_000);
    return () => {
      window.removeEventListener('online', syncConnectivity);
      window.removeEventListener('offline', syncConnectivity);
      window.clearInterval(interval);
      release();
      releaseReservation();
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let epoch = 0;
    // Until IndexedDB metadata is known, applying an update is fail-closed.
    // This lock survives leaving the scanner route and includes other owners'
    // pending/review records. Nothing here reads/decrypts a QR or participant.
    let release: (() => void) | undefined = acquirePwaUpdateLock();
    const check = () => {
      const currentEpoch = ++epoch;
      release ??= acquirePwaUpdateLock();
      void hasPendingSessionScans().then((pending) => {
        if (!alive || currentEpoch !== epoch) return;
        if (!pending) { release?.(); release = undefined; }
      }).catch(() => {
        // Storage inspection errors keep the update blocked.
      });
    };
    const visible = () => { if (document.visibilityState !== 'hidden' && !isPwaUpdateReserved()) check(); };
    check();
    window.addEventListener('event:session-outbox-changed', check);
    window.addEventListener('focus', visible);
    document.addEventListener('visibilitychange', visible);
    const interval = window.setInterval(visible, 15_000);
    return () => {
      alive = false;
      epoch++;
      window.clearInterval(interval);
      window.removeEventListener('event:session-outbox-changed', check);
      window.removeEventListener('focus', visible);
      document.removeEventListener('visibilitychange', visible);
      release?.();
    };
  }, []);

  useEffect(() => {
    if (!window.isSecureContext || !('serviceWorker' in navigator)) return;
    return attachPwaUpdateCoordinator(navigator.serviceWorker, hasPendingSessionScans);
  }, []);

  useEffect(() => () => { updateCleanup.current?.(); }, []);

  useEffect(() => {
    if (!waiting) return;
    // A different waiting worker is a new generation, not the already
    // activated version still running alongside this unchanged document.
    if (waiting.state !== 'activated' && waiting.state !== 'redundant') {
      setUpdateActivated(false);
      setUpdateFailure(undefined);
    }
    const changed = () => {
      if (waiting.state !== 'activated' && waiting.state !== 'redundant') return;
      setWaiting((current) => current === waiting ? undefined : current);
      if (waiting.state === 'activated') {
        updateCleanup.current?.();
        setUpdateActivated(true);
        setUpdatePreparing(false);
        setUpdateFailure(undefined);
      }
    };
    waiting.addEventListener('statechange', changed);
    changed();
    return () => waiting.removeEventListener('statechange', changed);
  }, [waiting]);

  useEffect(() => {
    const capturePrompt = (event: Event) => { event.preventDefault(); setInstallPrompt(event as InstallPromptEvent); };
    const installed = () => { setStandalone(true); setInstallPrompt(undefined); };
    window.addEventListener('beforeinstallprompt', capturePrompt);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', capturePrompt);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD || !window.isSecureContext || !('serviceWorker' in navigator)) return;
    let alive = true;
    let registration: ServiceWorkerRegistration | undefined;
    let installing: ServiceWorker | null = null;
    const syncWaiting = () => { if (alive) setWaiting(registration?.waiting ?? undefined); };
    const installingChanged = () => { if (installing?.state === 'installed') syncWaiting(); };
    const updateFound = () => {
      installing?.removeEventListener('statechange', installingChanged);
      installing = registration?.installing ?? null;
      installing?.addEventListener('statechange', installingChanged);
      syncWaiting();
    };
    let lastCheck = 0;
    const check = () => {
      if (!registration || !navigator.onLine || document.visibilityState === 'hidden' || Date.now() - lastCheck < 15 * 60_000) return;
      lastCheck = Date.now();
      void registration.update().catch(() => undefined);
    };
    void navigator.serviceWorker.register('/event-sw.js', { scope: '/', updateViaCache: 'none' }).then((value) => {
      if (!alive) return;
      registration = value;
      syncWaiting();
      registration.addEventListener('updatefound', updateFound);
      updateFound();
      check();
    }).catch(() => {
      // A failed registration must not interfere with online attendance.
      // It does not create a false offline-readiness success state.
    });
    window.addEventListener('online', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      alive = false;
      registration?.removeEventListener('updatefound', updateFound);
      installing?.removeEventListener('statechange', installingChanged);
      window.removeEventListener('online', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  const requestInstall = useCallback(async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(undefined);
  }, [installPrompt]);

  const activateUpdate = useCallback(() => {
    if (!waiting || !canApplyPwaUpdate() || isPwaUpdateReserved() || updateFlight.current) return;
    updateFlight.current = true;
    setUpdatePreparing(true);
    setUpdateFailure(undefined);
    let alive = true;
    const cleanup = () => {
      if (!alive) return;
      alive = false;
      clearTimeout(timer);
      waiting.removeEventListener('statechange', activated);
      updateFlight.current = false;
      if (updateCleanup.current === cleanup) updateCleanup.current = undefined;
    };
    const activated = () => {
      if (!alive) return;
      if (waiting.state === 'redundant') {
        cleanup();
        setUpdatePreparing(false);
        setUpdateFailure('verification_failed');
        setWaiting((current) => current === waiting ? undefined : current);
        return;
      }
      if (waiting.state !== 'activated') return;
      cleanup();
      setWaiting(undefined);
      setUpdateActivated(true);
      setUpdatePreparing(false);
    };
    const timer = setTimeout(() => {
      if (!alive) return;
      if (waiting.state === 'activated') { activated(); return; }
      cleanup();
      setUpdatePreparing(false);
      setUpdateFailure('verification_failed');
    }, PWA_UPDATE_MAX_DURATION_MS);
    updateCleanup.current = cleanup;
    waiting.addEventListener('statechange', activated);
    void requestUpdateActivation(waiting).then((result) => {
      if (!alive) return;
      if (result !== 'accepted') {
        cleanup();
        setUpdatePreparing(false);
        setUpdateFailure(result);
      } else if (waiting.state === 'activated') activated();
    }).catch(() => {
      if (!alive) return;
      cleanup();
      setUpdatePreparing(false);
      setUpdateFailure('verification_failed');
    });
    // Deliberately never reload/claim from a worker event. A second explicit
    // user action is required and is checked against the operation lock again.
  }, [waiting]);

  const reloadUpdatedApp = useCallback(() => {
    if (updateActivated && canApplyPwaUpdate() && !isPwaUpdateReserved()) window.location.reload();
  }, [updateActivated]);

  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return <PwaContext.Provider value={{ offline, updateAvailable: Boolean(waiting), updateActivated, updateBlocked, updatePreparing, updateCoordinating, updateFailure,
    canInstall: Boolean(installPrompt) && !standalone, iosInstallHint: ios && !standalone,
    requestInstall, activateUpdate, reloadUpdatedApp }}>{children}</PwaContext.Provider>;
}

export function usePwa(): PwaContextValue {
  const context = useContext(PwaContext);
  if (!context) throw new Error('usePwa requires PwaProvider');
  return context;
}
